import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import TeamNoteFormDialog from "@/components/dialogs/TeamNoteFormDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, NotebookPen, Target } from "lucide-react";

export default function TeamNotesPanel({ category, clientsMap, clients }) {
  const [notes, setNotes] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  const load = () => {
    api.get("/team-notes", { params: { category } }).then((r) => setNotes(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const deleteNote = async (id) => {
    try {
      await api.delete(`/team-notes/${id}`);
      toast.success("Note removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAdd = () => {
    setEditingNote(null);
    setFormOpen(true);
  };

  const openEdit = (note) => {
    setEditingNote(note);
    setFormOpen(true);
  };

  const Icon = category === "gtm" ? Target : NotebookPen;
  const label = category === "gtm" ? "GTM note" : "meeting note";

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={openAdd} data-testid={`add-${category}-note-button`} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add {category === "gtm" ? "GTM Note" : "Meeting Note"}
        </Button>
      </div>

      {notes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-14 text-center" data-testid={`${category}-notes-empty-state`}>
          <Icon className="h-6 w-6 text-[#4B5563]/40 mb-2" />
          <p className="text-sm text-[#4B5563]">No {label}s yet. Click "Add" to log the first one.</p>
        </div>
      )}

      <div className="space-y-3">
        {notes.map((n) => (
          <div key={n.id} data-testid={`note-row-${n.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">{n.title}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {n.meeting_date && <span className="text-xs text-[#4B5563]">{n.meeting_date}</span>}
                  {n.client_id && clientsMap?.[n.client_id] && (
                    <span className="text-[10px] font-medium bg-[#F7FEE7] text-[#00823B] px-1.5 py-0.5 rounded">{clientsMap[n.client_id]}</span>
                  )}
                  <span className="text-xs text-[#4B5563]">by {n.created_by}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button data-testid={`note-edit-${n.id}`} onClick={() => openEdit(n)} className="h-7 w-7 flex items-center justify-center rounded-md text-[#4B5563] hover:bg-[#F8F9FA] transition-colors duration-200">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`note-delete-${n.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 transition-colors duration-200">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{n.title}"?</AlertDialogTitle>
                      <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteNote(n.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <p className="text-sm text-[#1C1C1E]/80 mt-2 whitespace-pre-wrap leading-relaxed">{n.content}</p>
          </div>
        ))}
      </div>

      <TeamNoteFormDialog open={formOpen} onOpenChange={setFormOpen} category={category} note={editingNote} clients={clients || []} onSaved={load} />
    </div>
  );
}
